import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

/**
 * Knowledge Base suggestion controller for the ticket creation form.
 * Debounces subject/description input and fetches doc suggestions.
 *
 * data-controller="support--kb-suggest"
 * data-support--kb-suggest-url-value="/docs/search/suggestions"
 */
export default class extends Controller {
  static targets = ["panel", "results"]
  static values = { url: String }

  connect() {
    this._timeout = null
  }

  disconnect() {
    if (this._timeout) clearTimeout(this._timeout)
  }

  /**
   * Called on input events from subject/description fields.
   * Debounces 500ms then fetches suggestions.
   */
  search(event) {
    if (this._timeout) clearTimeout(this._timeout)

    const query = event.target.value.trim()
    if (query.length < 3) {
      this._hide()
      return
    }

    this._timeout = setTimeout(() => this._fetch(query), 500)
  }

  async _fetch(query) {
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
      const response = await get(url, { responseKind: "json" })

      if (!response.ok) {
        this._hide()
        return
      }

      const results = await response.json

      if (results.length === 0) {
        this._hide()
        return
      }

      this._render(results)
    } catch {
      this._hide()
    }
  }

  _render(results) {
    let html = ""
    results.forEach((doc) => {
      html += `
        <a href="/docs/${doc.slug}" target="_blank" rel="noopener noreferrer"
           class="block px-3 py-2 rounded-md hover:bg-muted/60 transition-colors group">
          <p class="text-sm font-medium text-foreground group-hover:text-primary transition-colors">${this._escape(doc.title)}</p>
          <p class="text-xs text-muted-foreground mt-0.5">${this._escape(doc.category || "")}</p>
        </a>
      `
    })
    this.resultsTarget.innerHTML = html
    this.panelTarget.classList.remove("hidden")
  }

  _hide() {
    this.panelTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
  }

  _escape(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
